import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Modal, Form, Alert, Accordion } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuoteLeft,
  faPhone,
  faEnvelope,
  faArrowRight,
  faArrowLeft,
  faStar,
  faUpRightFromSquare,
  faCheckCircle,
  faUserDoctor,
  faHandHoldingHeart,
  faSeedling,
  faCalendarCheck,
  faClipboardList,
  faWandMagicSparkles,
  faCreditCard,
  faPiggyBank,
  faHandHoldingDollar,
  faPercent,
  faCheck,
  faMapMarkerAlt,
  faClock,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';

import { getApprovedTestimonials, saveTestimonial } from '../../services/firebase/testimonialService';
import { getServices } from '../../services/firebase/serviceService';
import { getActiveServiceCategories } from '../../services/firebase/categoryService';
import { getActiveFaqs } from '../../services/firebase/faqService';
import { getDoctors } from '../../services/firebase/doctorService';
import { saveContactMessage } from '../../services/firebase/contactService';
import CherryCalculator from '../../components/CherryCalculator';
import BeforeAfterSlider from '../../components/common/BeforeAfterSlider';
import AgelessEmbed from '../../components/common/AgelessEmbed';

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [activeService, setActiveService] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [activeCategoryTab, setActiveCategoryTab] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);

  const handleOpenService = (svc) => setActiveService(svc);
  const handleCloseService = () => setActiveService(null);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [contactValidated, setContactValidated] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState(null);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setContactValidated(true);
      return;
    }
    setContactSubmitting(true);
    setContactError(null);
    try {
      await saveContactMessage(contactForm);
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setContactValidated(false);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setContactError('There was a problem sending your message. Please try again later.');
    } finally {
      setContactSubmitting(false);
    }
  };

  // ----- Patient review submission -----
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ author: '', title: '', content: '', rating: 0 });
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewValidated, setReviewValidated] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const openReviewModal = () => {
    setReviewSubmitted(false);
    setReviewError(null);
    setReviewValidated(false);
    setReviewForm({ author: '', title: '', content: '', rating: 0 });
    setReviewHover(0);
    setShowReviewModal(true);
  };
  const closeReviewModal = () => setShowReviewModal(false);

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || reviewForm.rating < 1) {
      e.stopPropagation();
      setReviewValidated(true);
      return;
    }
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      await saveTestimonial({
        author: reviewForm.author.trim(),
        title: reviewForm.title.trim(),
        content: reviewForm.content.trim(),
        rating: reviewForm.rating
      });
      setReviewSubmitted(true);
      setReviewForm({ author: '', title: '', content: '', rating: 0 });
      setReviewValidated(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      setReviewError('There was a problem submitting your review. Please try again later.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Testimonial slider scroll control
  const testimonialTrackRef = useRef(null);
  const scrollTestimonials = (dir) => {
    const track = testimonialTrackRef.current;
    if (!track) return;
    const card = track.querySelector('.tcard');
    const amount = card ? card.getBoundingClientRect().width + 24 : track.clientWidth;
    track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getApprovedTestimonials();
        setTestimonials(data);
      } catch (err) {
        console.error('Failed to load testimonials:', err);
      } finally {
        setLoadingTestimonials(false);
      }
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (err) {
        console.error('Failed to load services:', err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getActiveServiceCategories();
        setServiceCategories(data);
      } catch (err) {
        console.error('Failed to load service categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const data = await getActiveFaqs();
        setFaqs(data);
      } catch (err) {
        console.error('Failed to load FAQs:', err);
      } finally {
        setLoadingFaqs(false);
      }
    };
    fetchFaqs();
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const data = await getDoctors();
        setDoctors(data);
      } catch (err) {
        console.error('Failed to load doctors:', err);
      } finally {
        setLoadingDoctors(false);
      }
    };
    fetchDoctors();
  }, []);

  const wellnessFallbackDescription = 'Personalized, evidence-based care designed to help you feel your best.';

  const trustItems = [
    { icon: faUserDoctor, label: 'Board Certified Providers' },
    { icon: faWandMagicSparkles, label: 'Evidence-Based Care' },
    { icon: faHandHoldingHeart, label: 'Personalized Treatment' }
  ];

  // Build category tabs for the Services section. Falls back to a flat grid
  // when no categories are configured, so the site works either way.
  const buildServiceTabs = () => {
    const tabs = [];
    const categorizedIds = new Set();
    // Highlighted ("Best Selling") categories are pinned first, then the rest by order.
    const orderedCats = [...serviceCategories].sort((a, b) => {
      const ha = a.highlight ? 0 : 1;
      const hb = b.highlight ? 0 : 1;
      if (ha !== hb) return ha - hb;
      return (a.order !== undefined ? a.order : 999) - (b.order !== undefined ? b.order : 999);
    });
    orderedCats.forEach((cat) => {
      const catServices = (cat.serviceIds || [])
        .map((id) => services.find((s) => s.id === id))
        .filter(Boolean);
      (cat.serviceIds || []).forEach((id) => categorizedIds.add(id));
      if (catServices.length > 0) {
        tabs.push({ id: cat.id, name: cat.name, services: catServices, highlight: !!cat.highlight });
      }
    });
    const uncategorized = services.filter((s) => !categorizedIds.has(s.id));
    if (tabs.length > 0 && uncategorized.length > 0) {
      tabs.push({ id: '__other', name: 'Other Services', services: uncategorized, highlight: false });
    }
    return tabs;
  };

  const serviceTabs = buildServiceTabs();
  const useServiceTabs = serviceTabs.length > 0;
  const currentServiceTab =
    serviceTabs.find((t) => t.id === activeCategoryTab) || serviceTabs[0] || null;
  const displayedServices = useServiceTabs
    ? currentServiceTab
      ? currentServiceTab.services
      : []
    : services;

  return (
    <>
      {/* ============== HERO ============== */}
      <section
        className="hero"
        id="home"
        style={{ '--hero-photo': "url('/images/hero-office.jpg')" }}
      >
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6} className="mb-5 mb-lg-0 fade-in-up">
              <div className="hero-content">
                <span className="hero-eyebrow">
                   Offering services to improve life quality.
                </span>
                <h1>
                  Transform your <span className="accent">wellness</span> journey
                </h1>
                <p>
                  Personalized health and wellness care designed to help you
                  thrive emotionally, mentally, and physically &mdash; with a team that
                  treats the whole you.
                </p>
                <div className="hero-cta d-flex flex-wrap gap-3">
                  <Button
                    as={Link}
                    to="/appointment"
                    variant="primary"
                    size="lg"
                    className="btn-appointment-animated btn-pill"
                  >
                    Book Appointment <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </Button>
                  <Button
                    as={Link}
                    to="/#services"
                    variant="outline-primary"
                    size="lg"
                    className="btn-pill"
                  >
                    Explore Services
                  </Button>
                </div>
                <div className="hero-stats">
                  <div className="hero-stat">
                    <div className="num">98%</div>
                    <div className="label">Patient satisfaction</div>
                  </div>
                  <div className="hero-stat">
                    <div className="num">1+</div>
                    <div className="label">Years of care</div>
                  </div>
                  <div className="hero-stat">
                    <div className="num">1k+</div>
                    <div className="label">Lives supported</div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6} className="fade-in-up">
              <div className="orb-wrap">
                <div className="orb orb-ring2"></div>
                <div className="orb orb-ring"></div>
                <div className="orb orb-core"></div>
                <div className="orb-label">
                  <span className="bt">Breathe</span>
                  <span className="bs">You're in good hands</span>
                </div>
                <div className="orb-tag t1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#5a8a73" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="9"/>
                  </svg>
                  Antiaging
                </div>
                <div className="orb-tag t2">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#3f6a55" strokeWidth="2">
                    <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10Z" strokeLinejoin="round"/>
                  </svg>
                  Longeivity
                </div>
                <div className="orb-tag t3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#c9a96e" strokeWidth="2">
                    <path d="M3 12h4l2-7 4 14 2-7h6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Weightloss
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============== TRUST BAR ============== */}
      <section className="trust-bar">
        <Container>
          <Row className="g-4 justify-content-between align-items-center">
            {trustItems.map((item, idx) => (
              <Col lg key={idx} xs={6} md={4}>
                <div className="trust-item">
                  <div className="trust-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <span>{item.label}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ============== ABOUT / STORYTELLING ============== */}
      <section className="page-section" id="about">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <BeforeAfterSlider note="" />
              <div className="text-center mt-4">
                <span className="see-results-cta">
                  <AgelessEmbed />
                </span>
              </div>
            </Col>
            <Col lg={6}>
              <span className="section-eyebrow">About Our Practice</span>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '40px', lineHeight: 1.2, marginBottom: '20px' }}>
                Whole-person wellness, designed around <em>you</em>.
              </h3>
              <p style={{ color: 'var(--text-light)', fontSize: '17px', lineHeight: 1.8, marginBottom: '20px' }}>
                Life &amp; Wellness Center is a modern  wellness rooted in compassion and clinical excellence. We believe healing
                happens when you feel truly seen  and that great care should feel as
                personal as it is professional.
              </p>
              <ul className="list-unstyled" style={{ marginBottom: '28px' }}>
                {['Compassionate, judgment-free environment',
                  'Personalized treatment plans, not templates',
                  'Whole-person approach to mental and physical health',
                  'Empowering you to thrive — not just cope'
                ].map((point, i) => (
                  <li key={i} className="d-flex align-items-start mb-2" style={{ color: 'var(--text-color)' }}>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-3 mt-1" style={{ color: 'var(--primary-color)' }} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============== SERVICES ============== */}
      <section className="page-section bg-light" id="services">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Our Services</span>
            <h3>Personalized Wellness, Every Step of the Way</h3>
            <p>Comprehensive health and holistic care delivered with empathy, expertise, and modern science.</p>
          </div>

          {loadingServices ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading services...</span>
              </div>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-0">Our services will be available soon. Please check back shortly.</p>
            </div>
          ) : (
            <>
              {useServiceTabs && (
                <div className="svc-tabs" role="tablist">
                  {serviceTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={currentServiceTab && currentServiceTab.id === tab.id}
                      className={`svc-tab ${tab.highlight ? 'svc-tab--highlight' : ''} ${currentServiceTab && currentServiceTab.id === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveCategoryTab(tab.id)}
                    >
                      {tab.highlight && <FontAwesomeIcon icon={faStar} className="me-2" />}
                      {tab.name}
                    </button>
                  ))}
                </div>
              )}
              <Row className="g-4">
                {displayedServices.map((svc) => (
                  <Col lg={4} md={6} key={svc.id}>
                    <div
                      className="scard-premium"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenService(svc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenService(svc);
                        }
                      }}
                    >
                      <div className="scard-thumb">
                        {svc.acceptsInsurance && (
                          <span className="scard-insurance-tag">
                            <FontAwesomeIcon icon={faCheckCircle} /> Accepts Insurance
                          </span>
                        )}
                        <img
                          src={svc.image || '/images/placeholder-service.svg'}
                          alt={svc.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/placeholder-service.svg';
                          }}
                        />
                      </div>
                      <h4 className="scard-title">{svc.title}</h4>
                      <p className="scard-desc">
                        {svc.description && svc.description.length > 130
                          ? `${svc.description.substring(0, 130).trim()}…`
                          : svc.description || wellnessFallbackDescription}
                      </p>
                      <button
                        type="button"
                        className="scard-more"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenService(svc);
                        }}
                      >
                        Learn more <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {!loadingServices && services.length > 0 && (
            <div className="text-center mt-5">
              <div className="app-cta">
                <p className="app-cta-text">For price and more information, please click this</p>
                <Button
                  href="http://lifeandwellnesscenter.repeatmd.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  size="lg"
                  className="btn-pill"
                >
                  View Prices &amp; More <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-2" />
                </Button>
              </div>
              <Button as={Link} to="/appointment" variant="outline-primary" className="btn-pill mt-4">
                Book a Service <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </Button>
              <p className="mt-4 mb-0" style={{ color: 'var(--text-light)' }}>
                Applying for a green card?{' '}
                <Link to="/immigration-medical-exam-odessa-tx" style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                  See our USCIS Immigration Medical Exam (I-693) <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </p>
            </div>
          )}
        </Container>
      </section>

      {/* ============== OUR CARE TEAM ============== */}
      <section className="page-section" id="provider">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Our Care Team</span>
            <h3>Meet the people behind your care</h3>
            <p>Board-certified, deeply compassionate, and dedicated to helping you feel truly well.</p>
          </div>

          {loadingDoctors ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading care team...</span>
              </div>
            </div>
          ) : doctors.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-0">Our care team will be available soon. Please check back shortly.</p>
            </div>
          ) : (
            <Row className="g-3 justify-content-center">
              {doctors.map((doc) => (
                <Col lg={2} md={4} sm={6} key={doc.id}>
                  <div className="team-card">
                    <div className="team-photo">
                      <img
                        src={doc.image || '/images/placeholder-doctor.svg'}
                        alt={doc.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder-doctor.svg';
                        }}
                      />
                    </div>
                    <div className="team-body">
                      <h4 className="team-name">{doc.name}</h4>
                      {doc.specialty && (
                        <p className="team-role">{doc.specialty}</p>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {!loadingDoctors && doctors.length > 0 && (
            <div className="text-center mt-5">
              <Button as={Link} to="/appointment" variant="primary" className="btn-pill">
                Book a Consultation <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="page-section bg-light" id="how">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">How It Works</span>
            <h3>Your Journey, Simplified</h3>
            <p>A modern, streamlined experience — from first visit to lasting transformation.</p>
          </div>

          <Row className="g-5 steps-row mt-3">
            {[
              { n: 1, icon: faCalendarCheck, title: 'Schedule Consultation', desc: 'Book online in minutes — virtual or in-person. No phone tag, no waiting rooms.' },
              { n: 2, icon: faClipboardList, title: 'Personalized Assessment', desc: 'A thoughtful, judgment-free conversation to understand your unique story and goals.' },
              { n: 3, icon: faSeedling, title: 'Begin Your Wellness Journey', desc: 'A care plan built for you — therapy, medication, coaching, or whatever fits best.' }
            ].map((s) => (
              <Col md={4} key={s.n}>
                <div className="step-card">
                  <div className="step-number">{s.n}</div>
                  <div style={{ fontSize: '32px', color: 'var(--primary-color)', marginTop: '14px' }}>
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                  <h5>{s.title}</h5>
                  <p>{s.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ============== TESTIMONIALS ============== */}
      <section className="page-section" id="stories">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Patient Success Stories</span>
            <h3>Real Stories, Real Transformation</h3>
            <p>Hear from those who chose to invest in their wellness journey.</p>
          </div>

          {!loadingTestimonials && testimonials.length === 1 && (
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div
                  className="tcard-stars mb-3"
                  aria-label={`${Math.round(testimonials[0].rating) || 5} out of 5 stars`}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <FontAwesomeIcon
                      key={n}
                      icon={faStar}
                      className={n <= (Math.round(testimonials[0].rating) || 5) ? '' : 'empty'}
                    />
                  ))}
                </div>
                <FontAwesomeIcon
                  icon={faQuoteLeft}
                  size="2x"
                  style={{ color: 'var(--primary-color)', opacity: 0.25, marginBottom: '20px' }}
                />
                <p>{testimonials[0].content}</p>
                <div className="testimonial-author">
                  <h5>{testimonials[0].author}</h5>
                  {testimonials[0].title && <span>{testimonials[0].title}</span>}
                </div>
              </div>
            </div>
          )}

          {!loadingTestimonials && testimonials.length > 1 && (
            <div className="tslider">
              <div className="tslider-track" ref={testimonialTrackRef}>
                {testimonials.map((t) => {
                  const stars = Math.round(t.rating) || 5;
                  return (
                    <div className="tcard" key={t.id}>
                      <div className="tcard-stars" aria-label={`${stars} out of 5 stars`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <FontAwesomeIcon
                            key={n}
                            icon={faStar}
                            className={n <= stars ? '' : 'empty'}
                          />
                        ))}
                      </div>
                      <FontAwesomeIcon
                        icon={faQuoteLeft}
                        style={{ color: 'var(--primary-color)', opacity: 0.2, fontSize: '22px', marginBottom: '14px' }}
                      />
                      <p className="tcard-quote">{t.content}</p>
                      <div className="tcard-author">
                        <h5>{t.author}</h5>
                        {t.title && <span>{t.title}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {testimonials.length > 3 && (
                <div className="tslider-nav">
                  <button type="button" className="tslider-btn" aria-label="Previous reviews" onClick={() => scrollTestimonials(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  <button type="button" className="tslider-btn" aria-label="Next reviews" onClick={() => scrollTestimonials(1)}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="text-center mt-5">
            <p className="mb-3" style={{ color: 'var(--text-light)' }}>
              Have you visited us? We'd love to hear about your experience.
            </p>
            <Button variant="primary" className="btn-pill" onClick={openReviewModal}>
              Write a Review <FontAwesomeIcon icon={faPaperPlane} className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* ===== Write a Review Modal ===== */}
      <Modal show={showReviewModal} onHide={closeReviewModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Share Your Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reviewSubmitted ? (
            <Alert variant="success" className="mb-0">
              Thank you for your review! It will appear on our site once our team has approved it.
            </Alert>
          ) : (
            <>
              {reviewError && <Alert variant="danger">{reviewError}</Alert>}
              <Form noValidate validated={reviewValidated} onSubmit={handleReviewSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Rating</Form.Label>
                  <div className="rating-input" role="radiogroup" aria-label="Star rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        className={`star ${n <= (reviewHover || reviewForm.rating) ? 'filled' : ''}`}
                        onClick={() => setReviewForm((prev) => ({ ...prev, rating: n }))}
                        onMouseEnter={() => setReviewHover(n)}
                        onMouseLeave={() => setReviewHover(0)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      >
                        <FontAwesomeIcon icon={faStar} />
                      </button>
                    ))}
                  </div>
                  {reviewValidated && reviewForm.rating < 1 && (
                    <div className="text-danger small mt-1">Please select a rating.</div>
                  )}
                </Form.Group>
                <Form.Group className="mb-3" controlId="reviewAuthor">
                  <Form.Label>Your Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="author"
                    value={reviewForm.author}
                    onChange={handleReviewChange}
                    required
                    maxLength={80}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter your name.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="reviewTitle">
                  <Form.Label>
                    Title <span className="text-muted">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="e.g., Weight Loss Patient"
                    value={reviewForm.title}
                    onChange={handleReviewChange}
                    maxLength={80}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="reviewContent">
                  <Form.Label>Your Review</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="content"
                    value={reviewForm.content}
                    onChange={handleReviewChange}
                    required
                    minLength={10}
                    maxLength={1000}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please write at least a few words about your experience.
                  </Form.Control.Feedback>
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" onClick={closeReviewModal} disabled={reviewSubmitting}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={reviewSubmitting}>
                    {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ============== FINANCING ============== */}
      <section className="page-section bg-light" id="financing">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Financing & Payment</span>
            <h3>Care That Fits Your <em>budget</em></h3>
            <p>Wellness shouldn't wait for payday. Estimate your monthly payment in seconds.</p>
          </div>

          <Row className="g-4 align-items-stretch financing-grid">
            <Col lg={5}>
              <div className="financing-info-stack">
                {[
                  {
                    icon: faCreditCard,
                    title: 'Flexible Payment Plans',
                    desc: 'Spread treatment costs across manageable monthly payments.'
                  },
                  {
                    icon: faPiggyBank,
                    title: 'HSA & FSA Friendly',
                    desc: 'Use pre-tax health savings to make care more affordable.'
                  },
                  {
                    icon: faHandHoldingDollar,
                    title: 'Cherry Financing',
                    desc: 'Quick approval. Plans starting at $0 down for qualified applicants.'
                  }
                ].map((card) => (
                  <div className="financing-card-compact" key={card.title}>
                    <div className="financing-icon-sm">
                      <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <div className="financing-card-text">
                      <h5>{card.title}</h5>
                      <p>{card.desc}</p>
                    </div>
                  </div>
                ))}

                <div className="cherry-callout-card">
                  <div className="cherry-callout-header">
                    <span className="cherry-callout-logo">
                      <FontAwesomeIcon icon={faPercent} />
                    </span>
                    <span className="cherry-callout-brand">Cherry</span>
                  </div>
                  <h4 className="cherry-callout-title">Treat now, pay later</h4>
                  <ul className="cherry-callout-list">
                    <li>
                      <FontAwesomeIcon icon={faCheck} className="cherry-callout-check" />
                      <span><strong>No hard credit checks</strong>, ever</span>
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faCheck} className="cherry-callout-check" />
                      <span><strong>True 0% APR</strong> options available</span>
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faCheck} className="cherry-callout-check" />
                      <span>Interest-bearing plans with APRs <strong>as low as 5.99%</strong></span>
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faCheck} className="cherry-callout-check" />
                      <span>Up to <strong>$30,000</strong> approvals</span>
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faCheck} className="cherry-callout-check" />
                      <span><strong>No hidden fees</strong></span>
                    </li>
                  </ul>
                  <a
                    href="https://pay.withcherry.com/life-and-wellness-center?utm_source=merchant&utm_medium=website"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cherry-callout-cta"
                  >
                    See if you qualify
                  </a>
                </div>
              </div>
            </Col>
            <Col lg={7}>
              <div className="financing-widget-wrap">
                <div className="financing-widget-header">
                  <span className="section-eyebrow">Estimate Your Plan</span>
                  <h4>Calculate Your Monthly Payment</h4>
                </div>
                <CherryCalculator />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============== FAQ ============== */}
      {!loadingFaqs && faqs.length > 0 && (
        <section className="page-section" id="faq">
          <Container>
            <div className="section-header">
              <span className="section-eyebrow">FAQ</span>
              <h3>Frequently Asked Questions</h3>
              <p>Answers to the questions we hear most. Still curious? Reach out anytime.</p>
            </div>
            <div className="faq-wrap">
              <Accordion flush>
                {faqs.map((faq, i) => (
                  <Accordion.Item eventKey={String(i)} key={faq.id}>
                    <Accordion.Header>{faq.question}</Accordion.Header>
                    <Accordion.Body
                      style={
                        faq.answerColor
                          ? { color: faq.answerColor === 'white' ? '#ffffff' : '#1a1a1a' }
                          : undefined
                      }
                    >
                      {faq.answer}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          </Container>
        </section>
      )}

      {/* ============== APPOINTMENT QUICK-CONTACT ============== */}
      <section className="appointment-section" id="booking">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h3>Ready When You Are.</h3>
              <p className="mb-4">
                Book your appointment online — virtual or in-person — and start feeling
                like yourself again. Most appointments available within 48 hours.
              </p>
              <Button
                as={Link}
                to="/appointment"
                size="lg"
                className="appointment-cta-btn"
              >
                Book an Appointment <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </Button>
            </Col>
            <Col lg={6}>
              <Row>
                <Col md={6} className="mb-3">
                  <div
                    className="p-4 d-flex align-items-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: '16px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.18)' }}
                  >
                    <FontAwesomeIcon icon={faPhone} className="me-3" size="lg" />
                    <div>
                      <h5 className="mb-1" style={{ color: 'white' }}>Call Us</h5>
                      <p className="mb-0" style={{ color: 'rgba(255,255,255,0.9)' }}>(432) 653-5487</p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div
                    className="p-4 d-flex align-items-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: '16px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.18)' }}
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="me-3" size="lg" />
                    <div>
                      <h5 className="mb-1" style={{ color: 'white' }}>Email</h5>
                      <p className="mb-0" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>lifeandwellnesscenter78@gmail.com</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============== CONTACT ============== */}
      <section className="page-section bg-light contact-section" id="contact">
        <Container>
          <div className="section-header">
            <span className="section-eyebrow">Get In Touch</span>
            <h3>We'd Love to <em>Hear From You</em></h3>
            <p>Have a question or ready to take the next step? Reach out and we'll get back to you shortly.</p>
          </div>

          <Row className="g-4 contact-info-row">
            <Col lg={4} md={6}>
              <div className="contact-info-tile">
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <h5>Our Location</h5>
                <p>6010 E Highway 191, Suite 240<br />Odessa, Texas</p>
              </div>
            </Col>
            <Col lg={4} md={6}>
              <div className="contact-info-tile">
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <h5>Phone</h5>
                <p>(432) 653-5487</p>
              </div>
            </Col>
            <Col lg={4} md={12}>
              <div className="contact-info-tile">
                <div className="contact-info-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <h5>Email</h5>
                <p>lifeandwellnesscenter78@gmail.com</p>
              </div>
            </Col>
          </Row>

          <Row className="g-4 align-items-stretch contact-grid mt-2">
            <Col lg={6}>
              <div className="contact-form-card">
                <div className="contact-form-header">
                  <span className="section-eyebrow">Send a Message</span>
                  <h4>How can we help you?</h4>
                </div>
                {contactSubmitted && (
                  <Alert variant="success" onClose={() => setContactSubmitted(false)} dismissible>
                    Your message has been sent. We'll get back to you soon!
                  </Alert>
                )}
                {contactError && (
                  <Alert variant="danger" onClose={() => setContactError(null)} dismissible>
                    {contactError}
                  </Alert>
                )}
                <Form noValidate validated={contactValidated} onSubmit={handleContactSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={contactForm.name}
                          onChange={handleContactChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your name.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={contactForm.email}
                          onChange={handleContactChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={contactForm.phone}
                          onChange={handleContactChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your phone number.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={contactForm.subject}
                          onChange={handleContactChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a subject.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Your Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your message.
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={contactSubmitting}
                  >
                    {contactSubmitting ? 'Sending...' : (
                      <>
                        Send Message <FontAwesomeIcon icon={faPaperPlane} className="ms-2" />
                      </>
                    )}
                  </Button>
                </Form>
              </div>
            </Col>
            <Col lg={6}>
              <div className="contact-side-stack">
                <div className="contact-map-card">
                  <iframe
                    src="https://www.google.com/maps?q=6010+E+Highway+191,+Suite+240,+Odessa,+Texas&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Life & Wellness Center Location"
                  ></iframe>
                </div>
                <div className="contact-hours-card">
                  <div className="contact-hours-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div className="contact-hours-text">
                    <h5>Business Hours</h5>
                    <div className="contact-hours-row">
                      <span>Monday – Friday</span>
                      <span>9:00 AM – 5:00 PM</span>
                    </div>
                    <div className="contact-hours-row">
                      <span>Saturday – Sunday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============== FINAL EMOTIONAL CTA ============== */}
      <section className="final-cta">
        <Container style={{ position: 'relative', zIndex: 2 }}>
          <span className="section-eyebrow">
            Your Next Chapter Starts Here
          </span>
          <h3>You Deserve to Feel Your Best.</h3>
          <p>
            Take the first step toward a healthier, more balanced life. We're here
            whenever you're ready — with care that meets you exactly where you are.
          </p>
          <Button
            as={Link}
            to="/appointment"
            variant="primary"
            size="lg"
            className="btn-appointment-animated"
          >
            Schedule Your Consultation <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
        </Container>
      </section>

      {/* ============== SERVICE DETAILS MODAL ============== */}
      <Modal
        show={!!activeService}
        onHide={handleCloseService}
        centered
        size="lg"
        className="service-details-modal"
      >
        {activeService && (
          <>
            <div className="service-modal-media">
              <img
                src={activeService.image || '/images/placeholder-service.svg'}
                alt={activeService.title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/placeholder-service.svg';
                }}
              />
              <button
                type="button"
                className="service-modal-close"
                aria-label="Close"
                onClick={handleCloseService}
              >
                &times;
              </button>
            </div>
            <Modal.Body>
              <span className="section-eyebrow">Our Services</span>
              <h3 className="service-modal-title">{activeService.title}</h3>
              <p className="service-modal-description">
                {activeService.description || wellnessFallbackDescription}
              </p>
              <p className="service-modal-pricing-note">
                For price and more information, please click this
              </p>
              <div className="service-modal-actions">
                <Button
                  href="http://lifeandwellnesscenter.repeatmd.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                >
                  View Prices &amp; More <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-2" />
                </Button>
                <Button
                  as={Link}
                  to="/appointment"
                  variant="outline-primary"
                  onClick={handleCloseService}
                >
                  Book This Service <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Button>
                <Button variant="outline-secondary" onClick={handleCloseService}>
                  Close
                </Button>
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
};

export default Home;
